import { TEN_SECONDS_IN_MS } from '#shared/constants/durations'
import { applyRentalsApiHeadersToFetchOptions } from '#shared/lib/rentals-api-headers'

import type { $Fetch, NitroFetchRequest } from 'nitropack'
import type { RentalsApiHeadersContext } from '#shared/types/rentals-api-headers'

export default defineNuxtPlugin(() => {
  const runtimeConfig = useRuntimeConfig()
  const clients = runtimeConfig.public.openFetch
  const localFetch = useRequestFetch()
  const { isMobileOrTablet } = useDevice()
  const { rawToken } = useAuthState()
  const platformCookie = usePlatformCookie()
  const headers = useRequestHeaders()
  const { $captureError } = useNuxtApp()

  /**
   * Reports a failed API response.
   *
   * ofetch hands back the `Response` rather than an Error, so the status has to
   * be lifted into a real one. Reporting the response directly leaves the log
   * record with no message and no stack.
   *
   * @param response - The failed response.
   * @param location - The client and hook the failure came from.
   */
  function captureResponseError(response: Response, location: string) {
    const status = `${response.status} ${response.statusText}`.trim()

    $captureError(new Error(`Request failed with ${status}`), {
      location,
      status: response.status,
      url: response.url,
    })
  }

  function buildRentalsApiHeadersContext(): RentalsApiHeadersContext {
    return {
      rentalsApiVersion: runtimeConfig.public.rentalsApiVersion,
      authorizationToken: rawToken.value,
      userAgent: headers['user-agent'],
      cfCountry: headers['cf-ipcountry'],
      cfRegion: headers['cf-region'],
      cfCity: headers['cf-ipcity'],
      platform: platformCookie.value ?? (isMobileOrTablet ? 'mobile_web' : 'web'),
      anonymousId: isMetaClient ? getSegmentAnonymousId() : undefined,
      includeCredentials: isMetaClient,
      cookie: isMetaServer ? headers['cookie'] : undefined,
    }
  }

  return {
    // All clients will be augmented with these headers
    provide: Object.entries(clients).reduce((acc, [name, options]) => ({
      ...acc,
      [name]: createOpenFetch((localOptions = {}) => {
        const {
          timeout: timeoutOption,
          ...fetchOptions
        } = localOptions

        if (name === 'gis') {
          return {
            ...options,
            ...fetchOptions,
            timeout: timeoutOption ?? TEN_SECONDS_IN_MS,
            onRequestError({ error }) {
              if (error.name === 'AbortError' || error.name === 'TimeoutError') {
                return
              }

              $captureError(error, {
                location: '$gis onRequestError',
              })
            },
            onResponseError({ response }) {
              captureResponseError(response, '$gis onResponseError')
            },
          }
        }

        else {
          return {
            ...options,
            ...fetchOptions,
            timeout: timeoutOption ?? TEN_SECONDS_IN_MS,
            onRequest({ options: requestOptions }) {
              applyRentalsApiHeadersToFetchOptions(requestOptions, buildRentalsApiHeadersContext())
            },
            onRequestError({ error }) {
              if (error.name === 'AbortError' || error.name === 'TimeoutError') {
                return
              }

              $captureError(error, {
                location: '$rental onRequestError',
              })
            },
            onResponseError({ response }) {
              captureResponseError(response, '$rental onResponseError')
            },
          }
        }
      }, localFetch as $Fetch<unknown, NitroFetchRequest>),
    }), {}),
  }
})
